import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BoardEntry } from "@/components/board";
import {
  categoriesQuery,
  categoryBoardQuery,
  categoryBySlugQuery,
  boardScopeIds,
} from "@/lib/queries";
import { getHotTopic, HOT_TOPICS } from "@/lib/hot-cluster";
import { getHotClusterContent } from "@/lib/hot-cluster.i18n";
import { BOARDS_OG_IMAGE, BOARDS_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useLocale, useT } from "@/i18n";
import { detectLocale } from "@/i18n/runtime";

export const Route = createFileRoute("/hot/$topic")({
  loader: ({ params }) => {
    const topic = getHotTopic(params.topic);
    if (!topic) throw notFound();
    return { topic: topic.slug };
  },
  head: ({ params }) => {
    const topic = getHotTopic(params.topic);
    if (!topic) {
      return buildPageMetadata({
        path: `/hot/${params.topic}`,
        title: "Page not found | BeHot.Now",
        description: "This page is not available on BeHot.Now.",
        noindex: true,
      });
    }
    const c = getHotClusterContent(detectLocale(), topic);
    return buildPageMetadata({
      path: `/hot/${topic.slug}`,
      title: c.title,
      description: c.description,
      ogTitle: c.h1,
      ogDescription: c.description,
      image: BOARDS_OG_IMAGE,
      imageAlt: BOARDS_OG_IMAGE_ALT,
      keywords: topic.keywords,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "HOT", path: "/hot" },
        { name: c.h1, path: `/hot/${topic.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: c.h1,
        url: `${SITE_URL}/hot/${topic.slug}`,
        description: c.description,
        isPartOf: { "@type": "WebSite", url: SITE_URL, name: "BeHot.Now" },
      },
    });
  },
  component: HotTopicPage,
});

function HotTopicPage() {
  const { topic: slug } = Route.useParams();
  const locale = useLocale();
  const { t } = useT();
  const base = getHotTopic(slug);
  const topic = base ? getHotClusterContent(locale, base) : null;

  if (!base || !topic) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("hotc.notFound")}</h1>
        <Button asChild variant="hot" className="mt-4">
          <Link to="/hot">{t("hotc.goToBoards")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          {t("hotc.home")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/hot" className="hover:text-foreground">
          {t("hotc.hot")}
        </Link>
      </nav>

      <header className="mt-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          <Flame className="size-3.5" /> BeHot.Now
        </span>
        <h1 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl">
          {topic.h1}
        </h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">{topic.intro}</p>
      </header>

      <div className="mt-6 flex flex-wrap gap-3">
        {base.board ? (
          <Button asChild variant="hot">
            <Link to="/c/$slug" params={{ slug: base.board }}>
              {t("hotc.openBoard")} <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
        ) : (
          <Button asChild variant="hot">
            <Link to="/hot">
              {t("hotc.seeWhatsHot")} <ArrowRight className="ml-1 size-4" />
            </Link>
          </Button>
        )}
        <Button asChild variant="outline">
          <Link to="/get-hot" search={{ category: base.board, listing: undefined }}>
            {t("hotc.getOnBoard")}
          </Link>
        </Button>
      </div>

      {base.board && <LiveBoardPreview boardSlug={base.board} />}

      <div className="mt-10 space-y-8">
        {topic.sections.map((section, i) => (
          <section key={i}>
            <h2 className="font-display text-xl font-bold sm:text-2xl">{section.h2}</h2>
            {section.body.map((p, j) => (
              <p key={j} className="mt-3 leading-relaxed text-muted-foreground">
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>

      <section className="mt-12">
        <h2 className="font-display text-xl font-bold sm:text-2xl">{t("hotc.questions")}</h2>
        <dl className="mt-4 space-y-4">
          {topic.faq.map(([q, a], i) => (
            <div key={i} className="card-surface p-4">
              <dt className="font-semibold">{q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("hotc.keepExploring")}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {base.related.map((r) => {
            const target = getHotTopic(r);
            if (!target) return null;
            return (
              <Link
                key={r}
                to="/hot/$topic"
                params={{ topic: r }}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {getHotClusterContent(locale, target).h1}
              </Link>
            );
          })}
          <Link
            to="/compare/hot/$competitor"
            params={{ competitor: "outbid-lol" }}
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("hotc.compareBoards")}
          </Link>
          <Link
            to="/how-it-works"
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("hotc.howItWorks")}
          </Link>
        </div>
      </section>

      {slug === "discover" && (
        <section className="mt-12">
          <h2 className="font-display text-xl font-bold sm:text-2xl">{t("hotc.everyBoard")}</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {HOT_TOPICS.filter((x) => x.board).map((x) => (
              <Link
                key={x.slug}
                to="/hot/$topic"
                params={{ topic: x.slug }}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                {getHotClusterContent(locale, x).h1}
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function LiveBoardPreview({ boardSlug }: { boardSlug: string }) {
  const { t } = useT();
  const { data: category } = useQuery(categoryBySlugQuery(boardSlug));
  const { data: categories = [] } = useQuery(categoriesQuery);
  const scope = boardScopeIds(categories, category?.id);
  const { data: board = [] } = useQuery(categoryBoardQuery(scope));
  const top = board.slice(0, 5);
  if (top.length === 0) return null;

  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-bold sm:text-2xl">{t("hotc.topRightNow")}</h2>
      <div className="mt-3 space-y-3">
        {top.map((row, i) => (
          <BoardEntry key={row.listing.id} row={row} rank={i + 1} categorySlug={boardSlug} />
        ))}
      </div>
      <Link
        to="/c/$slug"
        params={{ slug: boardSlug }}
        className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
      >
        {t("hotc.seeFullBoard")} <ArrowRight className="size-4" />
      </Link>
    </section>
  );
}
